import React from "react";
import { motion } from "framer-motion";
import { Phone, Check, Droplets, Package } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../../lib/contact";

const WhatsAppIcon = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M19.11 4.92A10 10 0 0 0 4.07 18.2L3 22l3.88-1.03A10 10 0 1 0 19.11 4.92Zm-7.1 15.34a8.27 8.27 0 0 1-4.21-1.15l-.3-.18-2.3.61.62-2.24-.2-.32a8.27 8.27 0 1 1 6.4 3.28Zm4.55-6.2c-.25-.12-1.47-.72-1.7-.8-.23-.09-.39-.13-.56.12-.16.24-.64.8-.78.96-.14.16-.29.18-.54.06-.25-.13-1.05-.39-2-1.23a7.5 7.5 0 0 1-1.38-1.72c-.14-.25-.02-.38.11-.5.11-.11.25-.29.37-.43.13-.14.17-.25.25-.41.08-.16.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.84-.2-.49-.41-.42-.56-.43h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.02 2.6.13.17 1.76 2.69 4.27 3.77 1.49.64 2.07.7 2.81.59.45-.07 1.47-.6 1.68-1.19.21-.58.21-1.08.15-1.18-.07-.1-.23-.16-.49-.28Z" />
  </svg>
);

const formatTL = (n) =>
  n.toLocaleString("tr-TR", { minimumFractionDigits: 0 }) + " ₺";

const procleanPrices = [
  {
    id: "proclean-5",
    size: "5 Litre",
    desc: "Bidon ambalaj · Profesyonel kullanım",
    price: 600,
    featured: false,
    image:
      "https://customer-assets.emergentagent.com/job_halipak-solutions/artifacts/cyia18fb_image.png",
  },
  {
    id: "proclean-30",
    size: "30 Litre",
    desc: "Toplu kullanım · Profesyonel boyut",
    price: 3000,
    featured: false,
    image:
      "https://customer-assets.emergentagent.com/job_halipak-solutions/artifacts/ie6obkvg_image.png",
  },
];

const hurcuPrices = [
  { id: "h-35", width: "35 cm", length: "2000 mt", units: "1000 adet · 6m² halı paketler", price: 2800 },
  { id: "h-40", width: "40 cm", length: "2000 mt", units: "1000 adet · 6m² halı paketler", price: 2900 },
  { id: "h-45", width: "45 cm", length: "2000 mt", units: "1000 adet · 6m² halı paketler", price: 3000 },
  { id: "h-60", width: "60 cm", length: "2000 mt", units: "1000 adet · 6m² halı paketler", price: 3600 },
];

const HurcuPricingBlock = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6 }}
    className="mt-14"
    data-testid="pricing-hurcu"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 grid place-items-center">
        <Package className="w-5 h-5 text-[#2563EB]" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
          Ürün 01 · Ana Ürün · Ambalaj
        </div>
        <h3 className="font-heading font-extrabold text-[#0A192F] text-2xl sm:text-3xl leading-tight">
          HALIPAK Halı Hurcu
        </h3>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {hurcuPrices.map((p) => (
        <div
          key={p.id}
          data-testid={`price-card-${p.id}`}
          className={`relative rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 ${
            p.featured
              ? "bg-[#0A192F] text-white border-[#0A192F] shadow-2xl shadow-[#0A192F]/30"
              : "bg-[#F8FAFC] border-slate-200 hover:border-[#2563EB]/40 hover:shadow-xl hover:shadow-[#0A192F]/5"
          }`}
        >
          {p.featured && (
            <div className="absolute -top-3 right-5 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg hidden">
              Popüler
            </div>
          )}
          <div
            className={`text-[11px] font-bold uppercase tracking-widest ${
              p.featured ? "text-white/60" : "text-slate-500"
            }`}
          >
            Genişlik
          </div>
          <div
            className={`mt-1 font-heading font-extrabold text-3xl tracking-tight ${
              p.featured ? "text-white" : "text-[#0A192F]"
            }`}
          >
            {p.width}
          </div>

          <div
            className={`mt-5 pt-5 border-t ${
              p.featured ? "border-white/15" : "border-slate-200"
            }`}
          >
            <div
              className={`font-heading font-extrabold text-3xl tracking-tight ${
                p.featured ? "text-white" : "text-[#0A192F]"
              }`}
            >
              {formatTL(p.price)}
            </div>
            <div
              className={`mt-1 text-[11px] ${
                p.featured ? "text-white/60" : "text-slate-500"
              }`}
            >
              KDV hariç · Liste fiyatı
            </div>
          </div>
        </div>
      ))}
    </div>

    <div className="mt-8 flex flex-col sm:flex-row gap-3">
      <a
        href={`tel:${PHONE_TEL}`}
        data-testid="pricing-hurcu-phone-cta"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A192F] text-white px-6 py-3 text-sm font-semibold hover:bg-[#112240] active:scale-95 transition-all"
      >
        <Phone className="w-4 h-4" /> Sipariş için Ara · {PHONE_DISPLAY}
      </a>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="pricing-hurcu-whatsapp-cta"
        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] hover:bg-[#0FA978] text-white px-6 py-3 text-sm font-semibold active:scale-95 transition-all"
      >
        <WhatsAppIcon className="w-4 h-4" /> WhatsApp&apos;tan Sipariş Ver
      </a>
    </div>
  </motion.div>
);

const ProcleanPricingBlock = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.6, delay: 0.1 }}
    className="mt-20"
    data-testid="pricing-proclean"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-[#2563EB]/10 grid place-items-center">
        <Droplets className="w-5 h-5 text-[#2563EB]" />
      </div>
      <div>
        <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
          Ürün 02 · Kimya
        </div>
        <h3 className="font-heading font-extrabold text-[#0A192F] text-2xl sm:text-3xl leading-tight">
          PROCLEAN Halı Leke Çıkarıcı
        </h3>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 gap-5">
      {procleanPrices.map((p) => (
        <div
          key={p.id}
          data-testid={`price-card-${p.id}`}
          className={`relative rounded-2xl border p-7 transition-all duration-300 hover:-translate-y-1 overflow-hidden ${
            p.featured
              ? "bg-[#0A192F] text-white border-[#0A192F] shadow-2xl shadow-[#0A192F]/30"
              : "bg-[#F8FAFC] border-slate-200 hover:border-[#2563EB]/40 hover:shadow-xl hover:shadow-[#0A192F]/5"
          }`}
        >
          {p.featured && (
            <div className="absolute -top-3 left-6 bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg z-20 hidden">
              En Avantajlı
            </div>
          )}

          <div className="flex items-stretch gap-3">
            <div className="flex-1 min-w-0">
              <div
                className={`text-[11px] font-bold uppercase tracking-widest ${
                  p.featured ? "text-white/60" : "text-slate-500"
                }`}
              >
                {p.size}
              </div>
              <div
                className={`mt-1.5 text-sm ${
                  p.featured ? "text-white/80" : "text-slate-600"
                }`}
              >
                {p.desc}
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span
                  className={`font-heading font-extrabold text-4xl sm:text-5xl tracking-tight ${
                    p.featured ? "text-white" : "text-[#0A192F]"
                  }`}
                >
                  {formatTL(p.price)}
                </span>
              </div>
              <div
                className={`mt-2 text-xs ${
                  p.featured ? "text-white/60" : "text-slate-500"
                }`}
              >
                KDV hariç · Liste fiyatı
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-2">
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid={`price-${p.id}-phone`}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold active:scale-95 transition-all ${
                    p.featured
                      ? "bg-white text-[#0A192F] hover:bg-slate-100"
                      : "bg-[#0A192F] text-white hover:bg-[#112240]"
                  }`}
                >
                  <Phone className="w-3.5 h-3.5" /> Sipariş ver
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`price-${p.id}-whatsapp`}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#10B981] hover:bg-[#0FA978] text-white px-5 py-2.5 text-xs font-semibold active:scale-95 transition-all"
                >
                  <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
                </a>
              </div>
            </div>

            {p.image && (
              <div className="hidden sm:flex shrink-0 w-32 lg:w-40 items-center justify-center -my-2 -mr-2">
                <img
                  src={p.image}
                  alt={`PROCLEAN ${p.size}`}
                  loading="lazy"
                  draggable="false"
                  className="max-h-56 lg:max-h-64 w-auto object-contain drop-shadow-xl select-none pointer-events-none"
                />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  </motion.div>
);

const Pricing = () => {
  return (
    <section
      id="fiyat-listesi"
      data-testid="pricing-section"
      className="py-20 lg:py-28 bg-white border-y border-slate-100 relative overflow-hidden"
    >
      <div className="absolute top-20 -left-20 w-[380px] h-[380px] rounded-full bg-[#2563EB]/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 -right-20 w-[420px] h-[420px] rounded-full bg-[#0A192F]/5 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative">
        <div className="max-w-3xl">
          <div className="text-xs font-bold tracking-[0.28em] uppercase text-[#2563EB]">
            Fiyat Listesi
          </div>
          <h2 className="mt-4 font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-[#0A192F] leading-[1.02] tracking-tight">
            Şeffaf fiyat, <span className="text-[#2563EB]">net teklif.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl">
            Aşağıdaki güncel fiyatlar liste fiyatlarıdır. Toptan alımlarda özel
            indirim ve bayilik fırsatları için bizi arayın.
          </p>
        </div>

        <HurcuPricingBlock />
        <ProcleanPricingBlock />

        {/* Notes */}
        <div className="mt-12 rounded-2xl border border-dashed border-slate-300 bg-[#F8FAFC] p-6 grid sm:grid-cols-3 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-slate-700">
              <strong className="text-[#0A192F]">Toptan satış</strong> ve halı yıkama firmalarına özel fiyat avantajı.
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-slate-700">
              <strong className="text-[#0A192F]">Bayilikler</strong> verilmektedir. İletişime geçin.
            </span>
          </div>
          <div className="flex items-start gap-3">
            <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
            <span className="text-slate-700">
              <strong className="text-[#0A192F]">Türkiye geneli</strong> hızlı ve güvenli gönderim.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
